// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import merge from 'lodash/merge';
import { withStyles } from '@material-ui/core/styles';
import withHeaderAndFooter from '../../HOCs/withHeaderAndFooter';
import isMobile from '../../HOCs/isMobile';
import { styles } from './styles.js';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Review from '../../blocks/review';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Infinite from 'react-infinite';
import Loader from '../../components/loader';
import { decodeTarget, isStagingEnv } from '../../utils';
import {LocalesConsumer} from '../../utils/locales';
import type {ILocalesTransformers, ITranslate} from '../../utils/locales';

type Props = {
    classes: Object,
    isMobile: ?boolean,
    recentReviews: Object,
    requestReviews: Function,
}

type State = {
    isMount: boolean,
}

class CommunityActivity extends React.Component<Props, State> {
    lastTimestamp: number;
    constructor(props: Props) {
        super(props);

        this.state = {
            isMount: false,
        };
    }

    componentDidMount() {
        const { requestReviews } = this.props;
        if (isStagingEnv()) {
            window.layout = this;
        }
        this.setState({ isMount: true });

        requestReviews();
    }

    generateReviews(): any {
        const { classes, recentReviews } = this.props;

        if (recentReviews.data) {
            return recentReviews.data.map((review: Object, index: number, reviews: Array<Object>): React.Node => {
                if ((index + 1) === reviews.length) {
                    this.lastTimestamp = review.timestamp * 1000;
                }
                return (
                    <ListItem className={classes.userCommentContainer} key={review.id}>
                        <Review reviewInfo={{
                            user: review.user,
                            website: decodeTarget(review.target),
                            text: review.comment,
                            date: new Date(review.timestamp),
                        }}/>
                    </ListItem>
                );
            });
        }
    }

    getSizes = (): Object => {
        const { isMobile } = this.props;
        const { isMount } = this.state;

        let defaultSizes = {
            listSize: 720,
            rootClass: 'desktopRoot',
        };

        switch (true) {
            case (isMount && isMobile):
                return merge({}, defaultSizes, {
                    listSize: window.innerHeight - 250,
                    rootClass: 'mobileRoot',
                });
            case (isMount && !isMobile):
                return merge({}, defaultSizes, {
                    listSize: window.innerHeight - 200,
                    rootClass: 'desktopRoot',
                });
            case (!isMount):
            default:
                return defaultSizes;
        }
    }

    getContent = (sizeSettings: Object, translate: ITranslate): any => {
        const { classes, recentReviews, requestReviews, isMobile, ...other } = this.props;

        switch (true) {
            case (!recentReviews.data && recentReviews.status === 'RECENT_REVIEWS_REQUESTED'):
                return <Loader
                            size="medium"
                            text={translate('components.loader.receiving.reviews')}
                        />;
            case (!recentReviews.data && recentReviews.status === 'RECENT_REVIEWS_FAILED'):
                return (
                    <Grid
                        container
                        spacing={2}
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                        className={classes.loader}
                    >
                        <Grid item>
                            <Typography variant="subheading">
                                {translate('pages.community.error')}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <img src="/images/error-face.png"/>
                        </Grid>
                    </Grid>
                );
            case (Boolean(recentReviews.data)):
                return (
                    <Grid
                        container
                        justifyContent="center"
                    >
                        <Grid
                            wrap={'nowrap'}
                            item
                            container
                            justifyContent="center"
                            direction="column"
                            className={classes.widthLimiter}
                        >
                            <Grid
                                item
                                className={classes.headerContainer}
                            >
                                <Typography
                                    variant='h6'
                                    gutterBottom
                                    component={'h1'}
                                >
                                    {translate('pages.community.title')}
                                </Typography>
                            </Grid>
                            <Grid
                                item
                                container
                            >
                                <List className={classes.userReviewList}>
                                    <Infinite
                                        containerHeight={sizeSettings.listSize}
                                        elementHeight={158}
                                        infiniteLoadBeginEdgeOffset={20}
                                        onInfiniteLoad={() => {
                                            if (recentReviews.status !== 'RECENT_REVIEWS_FAILED') {
                                                requestReviews(20, recentReviews.data.length);
                                            }
                                        }}
                                        loadingSpinnerDelegate={
                                            <Loader
                                                size="small"
                                                text={translate('components.loader.loading.reviews')}
                                                className={classes.loader}
                                            />
                                        }
                                        isInfiniteLoading={
                                            this.props.recentReviews.status === 'RECENT_REVIEWS_REQUESTED'
                                        }
                                    >
                                        {this.generateReviews()}
                                    </Infinite>
                                </List>
                            </Grid>
                        </Grid>
                    </Grid>
                );
        }
    }

    render(): React.Node {
        const { classes } = this.props;

        let sizeSettings = this.getSizes();

        return (
            <LocalesConsumer>
                {
                    ({translate}: ILocalesTransformers): React.Node => (
                        <div
                            className={classnames(classes.root, classes[sizeSettings.rootClass])}
                        >
                            {this.getContent(sizeSettings, translate)}
                        </div>
                    )
                }
            </LocalesConsumer>
        );
    }
}

function mapStateToProps(state: Object): Object {
    return {
        recentReviews: state.recentReviews || {},
    };
}

function mapDispatchToProps(dispatch: Function): Object {
    return {
        requestReviews: (limit: number, from: number) => {
            dispatch({ type: 'RECENT_REVIEWS_REQUESTED', data: { limit, from } });
        },
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(
    isMobile(withHeaderAndFooter(withStyles(styles, { withTheme: true })(CommunityActivity))));
